import {
    Box, Button,
    ButtonGroup, Chip, FormControl,
    IconButton, InputLabel,
    MenuItem,
    Modal, OutlinedInput, Select, SpeedDial, SpeedDialAction, SpeedDialIcon,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import Weekday from "./Weekday";
import {
    AddTask,
    ArrowLeft,
    ArrowRight,
    CalendarMonth,
    Circle,
    Home, ImportExport,
    Label, Logout, PersonAddAlt1,
    PlaylistAdd,
    Settings
} from "@mui/icons-material";
import useLoginRequired from "../useLoginRequired";
import {TimeManager} from "../timemanager";
import useToken from "../useToken";
import {useEffect, useMemo, useRef, useState} from "react";
import {DateTimePicker} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import useResource from "../useResource";
import SettingsMenu from "./SettingsMenu";
import SideMenu from "./SideMenu";
import LabelsMenu from "./LabelsMenu";
import CanvasImport from "./CanvasImport";
import { useSwipeable } from 'react-swipeable'
import InviteMenu from "./InviteMenu";

export default function Mobile() {
    document.title = 'Time Manager';

    //console.log('Mobile Refresh');

    useLoginRequired('/mobile');

    const [token,, clearToken] = useToken();

    const [currentDate, setCurrentDate] = useState(new Date());
    const weekDate = useMemo(() => {
        const weekDate = new Date(currentDate.getTime());
        weekDate.setTime(weekDate.getTime() - (weekDate.getDay() * 24 * 60 * 60 * 1000));
        weekDate.setHours(0, 0, 0, 1);

        return weekDate;
    }, [currentDate]);

    const hourDisplayRef = useRef();
    const [periodWindowSize, setPeriodWindowSize] = useState(0);
    const [scrollCallbacks, setScrollCallbacks] = useState([]);

    const [createMenuOpen, setCreateMenuOpen] = useState(false);
    const [createEditKey, setCreateEditKey] = useState(null);
    const [createType, setCreateType] = useState('event');

    const [periods, setPeriods] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [events, setEvents] = useState([]);

    const [taskGrid, setTaskGrid] = useState([[], [], [], [], [], [], []]);
    const [taskGridHeight, setTaskGridHeight] = useState(0);
    const [eventGrid, setEventGrid] = useState([[], [], [], [], [], [], []]);
    const [eventGridHeight, setEventGridHeight] = useState(0);

    const [tasksCollapsed, setTasksCollapsed] = useState(true);
    const [eventsCollapsed, setEventsCollapsed] = useState(true);

    const [createName, setCreateName] = useState(null);
    const [createStart, setCreateStart] = useState(null);
    const [createEnd, setCreateEnd] = useState(null);
    const [createDescription, setCreateDescription] = useState(null);
    const [createLocation, setCreateLocation] = useState(null);
    const [createLabels, setCreateLabels] = useState([]);
    const [createRecurringType, setCreateRecurringType] = useState(null);
    const [createRecurringParameter, setCreateRecurringParameter] = useState('');
    const [createRecurringEnd, setCreateRecurringEnd] = useState(null);

    const [labels, refreshLabels] = useResource(TimeManager.time.label.list.bind(null, token), []);

    const [draggingPeriod, setDraggingPeriod] = useState(null);

    const [settingsOpen, setSettingsOpen] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [labelsOpen, setLabelsOpen] = useState(false);
    const [canvasImportOpen, setCanvasImportOpen] = useState(false);

    // Prevent swipe on drag.
    const [dragSwipeCompensate, setDragSwipeCompensate] = useState(false);

    const refreshPeriods = () => {
        TimeManager.time.period.getBetween(weekDate, new Date(weekDate.getTime() + 1000*60*60*24*7), token)
            .then(setPeriods)
            .catch(() => {});
    };

    const refreshEvents = () => {
        TimeManager.time.event.getBetween(weekDate, new Date(weekDate.getTime() + 1000*60*60*24*7), token)
            .then(setEvents)
            .catch(() => {});
    };

    const refreshTasks = () => {
        TimeManager.time.task.getBetween(weekDate, new Date(weekDate.getTime() + 1000*60*60*24*7), token)
            .then(setTasks)
            .catch(() => {});
    };

    const getDays = (date) => Math.floor((date.getTime() - date.getTimezoneOffset() * 60 * 1000) / 1000 / 60 / 60 / 24);

    const generateTaskGrid = () => {
        const taskGrid = [[], [], [], [], [], [], []];

        const weekStartDay = getDays(weekDate);

        if (!tasks) {
            return;
        }

        tasks.forEach((task) => {
            const {start, end} = task;

            const startDay = getDays(start);
            const endDay = getDays(end);

            const weekRelativeBegin = Math.max(startDay, weekStartDay);
            const weekRelativeEnd = Math.min(endDay, weekStartDay + 6);

            const beginTaskGridSlot = taskGrid[weekRelativeBegin - weekStartDay];

            if (beginTaskGridSlot === undefined) {
                return;
            }

            // const undefinedIndex = beginTaskGridSlot.findIndex((slot) => slot === undefined);
            // const verticalOffset = undefinedIndex === -1 ? beginTaskGridSlot.length : undefinedIndex;

            for (let i = weekRelativeBegin; i <= weekRelativeEnd; ++i) {
                taskGrid[i - weekStartDay].push({
                    start: i === startDay,
                    end: i === endDay,
                    remaining: endDay - i,
                    task
                });
            }
        });

        let height = 0;
        taskGrid.forEach((slot) => height = Math.max(height, slot.length));

        setTaskGridHeight(height);
        setTaskGrid(taskGrid);
    };

    const generateEventGrid = () => {
        const eventGrid = [[], [], [], [], [], [], []];

        const weekStartDay = getDays(weekDate);

        events.forEach((event) => {
            const {start, end} = event;

            if (event.recurring_time) {
                return;
            }

            const startDay = getDays(start);
            const endDay = getDays(end);

            const weekRelativeBegin = Math.max(startDay, weekStartDay);
            const weekRelativeEnd = Math.min(endDay, weekStartDay + 6);

            const beginEventGridSlot = eventGrid[weekRelativeBegin - weekStartDay];

            if (beginEventGridSlot === undefined) {
                return;
            }

            const undefinedIndex = beginEventGridSlot.findIndex((slot) => slot === undefined);
            const verticalOffset = undefinedIndex === -1 ? beginEventGridSlot.length : undefinedIndex;

            for (let i = weekRelativeBegin; i <= weekRelativeEnd; ++i) {
                eventGrid[i - weekStartDay][verticalOffset] = {
                    start: i === startDay,
                    end: i === endDay,
                    remaining: endDay - i,
                    event
                };
            }
        });

        events.filter((event) => event.recurring_time).forEach((event) => {
            const recurringTime = event.recurring_time;

            if (recurringTime.startsWith('W:')) {
                for (let i = 0; i < 7; ++i) {
                    if (recurringTime.includes(`${i + 1}`)) {
                        const undefinedIndex = eventGrid[i].findIndex((slot) => slot === undefined);
                        const verticalOffset = undefinedIndex === -1 ? eventGrid[i].length : undefinedIndex;

                        const start = new Date(event.start.getTime());
                        start.setFullYear(weekDate.getFullYear(), weekDate.getMonth(), weekDate.getDate() + i);

                        if (start < event.start) {
                            continue;
                        }

                        const end = new Date(event.end.getTime());
                        end.setFullYear(weekDate.getFullYear(), weekDate.getMonth(), weekDate.getDate() + i);

                        eventGrid[i][verticalOffset] = {
                            start: true,
                            end: true,
                            remaining: 0,
                            event: {
                                ...event,
                                start,
                                end
                            },
                            recurringSource: event,
                        };
                    }
                }
            }
            else if (recurringTime.startsWith('Y:')) {
                const [month, date] = recurringTime.substring(2).split('-').map((value) => parseInt(value));

                for (let i = 0; i < 7; ++i) {
                    const searchDate = new Date(weekDate.getTime() + (i * 1000*60*60*24));

                    if (searchDate.getMonth() === month && searchDate.getDate() === date) {
                        const undefinedIndex = eventGrid[i].findIndex((slot) => slot === undefined);
                        const verticalOffset = undefinedIndex === -1 ? eventGrid[i].length : undefinedIndex;

                        const start = new Date(searchDate.getTime());
                        start.setHours(event.start.getHours(), event.start.getMinutes());

                        const end = new Date(searchDate.getTime());
                        start.setHours(event.end.getHours(), event.end.getMinutes());

                        eventGrid[i][verticalOffset] = {
                            start: true,
                            end: true,
                            remaining: 0,
                            event: {
                                ...event,
                                start,
                                end,
                            },
                            recurringSource: event,
                        };
                    }
                }
            }
        });

        let height = 0;
        eventGrid.forEach((slot) => height = Math.max(height, slot.length));

        setEventGridHeight(height);
        setEventGrid(eventGrid);
    };

    const scroll = (value) => scrollCallbacks.forEach((callback) => callback(value));
    const onScroll = (e) => scroll(e.currentTarget.scrollTop);

    const scrollCallback = (value) => {
        hourDisplayRef.current.scrollTop = value;
        scroll(value);
    };

    const refreshPeriodWindowSize = () => {
        setPeriodWindowSize(hourDisplayRef.current.offsetHeight);
    };

    useEffect(refreshPeriodWindowSize, [
        hourDisplayRef,
        weekDate,
        taskGrid,
        eventGrid,
        periods,
        tasksCollapsed,
        eventsCollapsed
    ]);

    useEffect(refreshPeriods, [weekDate, setPeriods, token]);
    useEffect(refreshEvents, [weekDate, setEvents, token]);
    useEffect(refreshTasks, [weekDate, setTasks, token]);

    useEffect(generateTaskGrid, [tasks, setTaskGrid, weekDate, setTaskGridHeight]);
    useEffect(generateEventGrid, [events, setEventGrid, weekDate, setTaskGridHeight]);

    useEffect(() => {
        const clearDraggingPeriod = () => setDraggingPeriod(null);

        window.addEventListener('mouseleave', clearDraggingPeriod);
        window.addEventListener('mouseup', clearDraggingPeriod);

        return () => {
            window.removeEventListener('mouseleave', clearDraggingPeriod);
            window.removeEventListener('mouseup', clearDraggingPeriod);
        };
    }, [setDraggingPeriod]);

    const openCreateMenu = (type, defaults = {}) => {
        return () => {
            setCreateType(type);
            setCreateEditKey(null);

            setCreateName(defaults.name || `New ${type}`);
            setCreateStart(dayjs(defaults.start || currentDate));

            const endDate = new Date(currentDate.getTime());
            endDate.setHours(23);
            endDate.setMinutes(59);
            setCreateEnd(dayjs(defaults.end || endDate));

            setCreateDescription(defaults.description || '');
            setCreateLocation(defaults.location || '');
            setCreateLabels(defaults.labels ? defaults.labels.map((label) => parseInt(label)) : []);

            setCreateRecurringType(null);
            setCreateRecurringParameter('');
            setCreateRecurringEnd(null);

            setCreateMenuOpen(true);
        };
    };

    const beginEdit = (type, key) => {
        return () => {
            setCreateType(type);
            setCreateEditKey(key);

            let current = undefined;

            switch (type) {
                case 'Event':
                    current = events.find(e => e.key === key);
                    break;
                case 'Task':
                    current = tasks.find(t => t.key === key);
                    break;
                case 'Period':
                    current = periods.find(p => p.key === key);
                    break;
                default:
                    break;
            }

            if (current === undefined) {
                return;
            }

            setCreateName(current.name);
            setCreateStart(dayjs(current.start));
            setCreateEnd(dayjs(current.end));

            setCreateDescription(current.description || '');
            setCreateLocation(current.location || '');
            setCreateLabels(current.labels.map((label) => parseInt(label)));

            if (type === 'Event' && current.recurring_time) {
                const [recurringType, recurringParameter] = current.recurring_time.split(':');

                setCreateRecurringType(recurringType);
                setCreateRecurringParameter(recurringParameter);

                setCreateRecurringEnd(current.recurring_end ? dayjs(current.recurring_end) : null);
            }

            setCreateMenuOpen(true);
        };
    };

    const alterItem = (itemType, data) => {
        switch (itemType) {
            case 'Event':
                TimeManager.time.event.alter(data, token).then(() => refreshEvents());
                break;
            case 'Task':
                TimeManager.time.task.alter(data, token).then(() => refreshTasks());
                break;
            case 'Period':
                TimeManager.time.period.alter(data, token).then(() => refreshPeriods());
                break;
            default:
                break;
        }
    };

    const submitCreate = () => {
        switch (createType) {
            case 'Event':
                TimeManager.time.event.alter({
                    key: createEditKey || undefined,
                    name: createName,
                    start: createStart.toDate(),
                    end: createEnd.toDate(),
                    description: createDescription.length > 0 ? createDescription : undefined,
                    location: createLocation.length > 0 ? createLocation : undefined,
                    labels: createLabels || [],
                    recurring_time: createRecurringType !== null ? `${createRecurringType}:${createRecurringParameter}` : undefined,
                    recurring_end: createRecurringEnd ? createRecurringEnd.toDate() : undefined,
                }, token).then(() => refreshEvents());
                break;
            case 'Task':
                TimeManager.time.task.alter({
                    key: createEditKey || undefined,
                    name: createName,
                    start: createStart.toDate(),
                    end: createEnd.toDate(),
                    description: createDescription.length > 0 ? createDescription : undefined,
                    labels: createLabels || [],
                }, token).then(() => refreshTasks());
                break;
            case 'Period':
                TimeManager.time.period.alter({
                    key: createEditKey || undefined,
                    name: createName,
                    start: createStart.toDate(),
                    end: createEnd.toDate(),
                    description: createDescription.length > 0 ? createDescription : undefined,
                    location: createLocation.length > 0 ? createLocation : undefined,
                    labels: createLabels || [],
                }, token).then(() => refreshPeriods());
                break;
            default:
                break;
        }

        setCreateMenuOpen(false);
    };

    const markComplete = (key, complete) => {
        TimeManager.time.task.alter({
            key,
            complete
        }, token).then(refreshTasks);
    };

    const submitDelete = (type, key) => {
        return () => {
            type = type || createType;
            key = key || createEditKey;

            switch (type) {
                case 'Event':
                    TimeManager.time.event.alter({
                        key: key,
                        delete: true,
                    }, token).then(() => refreshEvents());
                    break;
                case 'Task':
                    TimeManager.time.task.alter({
                        key: key,
                        delete: true,
                    }, token).then(() => refreshTasks());
                    break;
                case 'Period':
                    TimeManager.time.period.alter({
                        key: key,
                        delete: true,
                    }, token).then(() => refreshPeriods());
                    break;
                default:
                    break;
            }

            setCreateMenuOpen(false);
        };
    };

    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => !dragSwipeCompensate && setCurrentDate((oldDate) => new Date(oldDate.getTime() + 1000*60*60*24)),
        onSwipedRight: () => !dragSwipeCompensate && setCurrentDate((oldDate) => new Date(oldDate.getTime() - 1000*60*60*24)),
    });

    useEffect(() => {
        setDragSwipeCompensate(true);

        if (draggingPeriod === null) {
            const timeoutId = setTimeout(() => {
                setDragSwipeCompensate(false);
            }, 500);

            return () => clearTimeout(timeoutId);
        }
    }, [draggingPeriod, setDragSwipeCompensate]);

    const logout = () => {
        TimeManager.account.logout(token).then(() => {
            clearToken();
            window.location.reload();
        });
    };

    return (
        <>
            <Stack
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                }}
                direction="row"
            >
                {/*<Box
                    sx={{
                        background: '#222',
                        borderRight: 'solid 1px #444',
                        width: '350px',
                    }}
                >
                    <Sidebar
                        periods={periods}
                        tasks={tasks}
                        events={events}
                        labels={labels}
                    />
                </Box>*/}
                <Stack
                    direction="column"
                    sx={{
                        background: '#141517',
                        flexGrow: 100
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            height: '60px',
                            background: '#141517',
                        }}
                    >
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{
                                ml: '10px',
                                mr: '10px',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                <IconButton
                                    onClick={() => {
                                        setCurrentDate((oldDate) => new Date(oldDate.getTime() - 1000*60*60*24));
                                    }}
                                    sx={{
                                        color: 'white',
                                    }}
                                >
                                    <ArrowLeft />
                                </IconButton>
                                <IconButton
                                    onClick={() => {
                                        const newDate = new Date();
                                        newDate.setHours(0, 0, 0, 1);
                                        setCurrentDate(newDate);
                                    }}
                                    sx={{
                                        color: 'white',
                                    }}
                                >
                                    <Home />
                                </IconButton>
                                <IconButton
                                    onClick={() => {
                                        setCurrentDate((oldDate) => new Date(oldDate.getTime() + 1000*60*60*24));
                                    }}
                                    sx={{
                                        color: 'white',
                                    }}
                                >
                                    <ArrowRight />
                                </IconButton>
                                <DateTimePicker
                                    value={dayjs(currentDate)}
                                    onChange={(value) => {
                                        const newDate = value.toDate();
                                        newDate.setHours(0, 0, 0, 1);

                                        try {
                                            newDate.toISOString();
                                            setCurrentDate(newDate);
                                        } catch {

                                        }
                                    }}
                                    views={['day', 'month', 'year']}
                                    sx={{
                                        '.MuiOutlinedInput-notchedOutline': {
                                            border: 'none'
                                        },
                                        '.MuiOutlinedInput-input': {
                                            color: '#fff',
                                        },
                                        '.MuiSvgIcon-root': {
                                            color: '#fff',
                                        },
                                        ml: '20px',
                                        pt: '5px',
                                        width: '130px',
                                    }}
                                />
                            </Box>
                            <Box>

                            </Box>
                            <Box
                                sx={{
                                    'svg': {
                                        color: 'white',
                                    },
                                }}
                            >
                                <SideMenu mobile>
                                    {[
                                        [<Label />, 'Labels', () => setLabelsOpen(true)],
                                        [<ImportExport />, 'Canvas Import', () => setCanvasImportOpen(true)],
                                        [<Settings />, 'Settings', () => setSettingsOpen(true)],
                                        [<PersonAddAlt1 />, 'Invite User', () => setInviteOpen(true)],
                                        [<Logout />, 'Logout', logout],
                                    ]}
                                </SideMenu>
                            </Box>
                        </Stack>
                    </Box>
                    <Stack direction="row" sx={{ height: '100%' }}>
                        <Box
                            sx={{
                                background: '#141517',
                                marginTop: '32px',
                                display: 'flex',
                                flexDirection: 'column',
                                height: 'calc(100% - 32px)',
                                width: '30px',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    minHeight: `${Math.max(eventGridHeight * (eventsCollapsed ? 8 : 33) + 9, 90)}px`,
                                    maxHeight: '100%',
                                    cursor: 'pointer',
                                }}
                                onClick={() => setEventsCollapsed((collapsed) => !collapsed)}
                            >
                                <Typography
                                    color="#ccc"
                                    textTransform="uppercase"
                                    ml="3px"
                                    fontWeight="500"
                                    sx={{
                                        writingMode: 'vertical-rl',
                                        textOrientation: 'mixed',
                                        transform: 'rotate(180deg)',
                                        userSelect: 'none',
                                        fontFamily: '"Inter", sans-serif',
                                    }}
                                >
                                    Events
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    minHeight: `${Math.max(taskGridHeight * (tasksCollapsed ? 8 : 33) + 9, 90)}px`,
                                    cursor: 'pointer',
                                }}
                                onClick={() => setTasksCollapsed((collapsed) => !collapsed)}
                            >
                                <Typography
                                    color="#ccc"
                                    textTransform="uppercase"
                                    ml="3px"
                                    fontWeight="700"
                                    sx={{
                                        writingMode: 'vertical-rl',
                                        textOrientation: 'mixed',
                                        transform: 'rotate(180deg)',
                                        userSelect: 'none',
                                        fontFamily: '"Inter", sans-serif',
                                    }}
                                >
                                    Tasks
                                </Typography>
                            </Box>
                            <Box
                                ref={hourDisplayRef}
                                sx={{
                                    overflowY: 'scroll',
                                    '::-webkit-scrollbar': {
                                        display: 'none',
                                    },
                                    msOverflowStyle: 'none',
                                    scrollbarWidth: 'none',
                                    flexGrow: '100',
                                    userSelect: 'none',
                                }}
                                onResize={(e) => setPeriodWindowSize(e.currentTarget.offsetHeight)}
                                onScroll={onScroll}
                            >
                                <div
                                    style={{
                                        position: 'relative',
                                        height: 'calc(24 * 60px + 80px)'
                                    }}
                                >
                                    {[...new Array(24).keys()].map((hour) => (
                                        <Typography
                                            key={hour}
                                            width="100%"
                                            height="60px"
                                            color="white"
                                            sx={{
                                                position: 'absolute',
                                                zIndex: '20',
                                                top: `${hour * 60}px`,
                                                left: '0',
                                                pt: 0,
                                                lineHeight: 1,
                                                textAlign: 'center',
                                                fontFamily: '"Roboto Mono", monospace',
                                                fontWeight: '300',
                                            }}
                                        >
                                            {`${hour}`.padStart(2, '0')}
                                        </Typography>
                                    ))}
                                </div>
                            </Box>
                        </Box>
                        <div
                            style={{
                                flexGrow: 100,
                                height: '100%'
                            }}
                        >
                            <Box
                                sx={{
                                    borderTop: 'solid 1px #444',
                                    borderLeft: 'solid 1px #444',
                                }}
                                {...swipeHandlers}
                            >
                                <Weekday
                                    periods={periods}
                                    weekDate={weekDate}
                                    day={currentDate.getDay()}
                                    periodWindowSize={periodWindowSize}
                                    addScrollCallback={(callback) => scrollCallbacks[0] || setScrollCallbacks([callback]) }
                                    scrollCallback={scrollCallback}
                                    beginEdit={beginEdit}
                                    submitDelete={submitDelete}
                                    markComplete={markComplete}
                                    openCreateMenu={openCreateMenu}
                                    taskSlots={taskGrid[currentDate.getDay()]}
                                    eventSlots={eventGrid[currentDate.getDay()]}
                                    taskGridHeight={taskGridHeight}
                                    eventGridHeight={eventGridHeight}
                                    tasksCollapsed={tasksCollapsed}
                                    eventsCollapsed={eventsCollapsed}
                                    alterItem={alterItem}
                                    draggingPeriod={draggingPeriod}
                                    setDraggingPeriod={setDraggingPeriod}
                                    labels={labels}
                                    mobile
                                    independentLabel
                                />
                            </Box>

                            <SpeedDial
                                ariaLabel="Create menu"
                                sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 250 }}
                                icon={<SpeedDialIcon />}
                            >
                                <SpeedDialAction
                                    icon={<CalendarMonth />}
                                    tooltipTitle={'Create Event'}
                                    onClick={openCreateMenu('Event')}
                                />
                                <SpeedDialAction
                                    icon={<AddTask />}
                                    tooltipTitle={'Create Task'}
                                    onClick={openCreateMenu('Task')}
                                />
                                <SpeedDialAction
                                    icon={<PlaylistAdd />}
                                    tooltipTitle={'Create Period'}
                                    onClick={openCreateMenu('Period')}
                                />
                            </SpeedDial>
                        </div>
                    </Stack>
                </Stack>
            </Stack>

            <CanvasImport
                open={canvasImportOpen}
                onClose={() => setCanvasImportOpen(false)}
                mobile
            />
            <SettingsMenu
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                mobile
            />
            <LabelsMenu
                open={labelsOpen}
                onClose={() => setLabelsOpen(false)}
                labels={labels}
                refreshLabels={refreshLabels}
                mobile
            />
            <InviteMenu
                open={inviteOpen}
                onClose={() => setInviteOpen(false)}
                mobile
            />

            <Modal
                open={createMenuOpen}
                onClose={() => setCreateMenuOpen(false)}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        bgcolor: 'background.paper',
                        border: '1px solid #000',
                        boxShadow: 24,
                        borderRadius: '5px',
                        p: 4,
                        overflowY: 'scroll',
                    }}
                >
                    <Typography variant="h6" component="h2" mb="20px">
                        {createEditKey !== null ? 'Edit' : 'Create'} {createType}
                    </Typography>
                    {createEditKey === null &&
                        <ButtonGroup
                            size="small"
                            sx={{
                                mb: '30px',
                            }}
                        >
                            {['Event', 'Task', 'Period'].map((type) => (
                                <Button
                                    key={type}
                                    onClick={() => setCreateType(type)}
                                    variant={createType === type ? 'contained' : 'outlined'}
                                >
                                    {type}
                                </Button>
                            ))}
                        </ButtonGroup>
                    }

                    <TextField
                        label="Name"
                        value={createName}
                        onChange={(e) => setCreateName(e.target.value)}
                        sx={{
                            mb: '20px',
                        }}
                        fullWidth
                    />

                    <Stack
                        direction="row"
                        sx={{
                            mb: '20px',
                        }}
                    >
                        <DateTimePicker
                            label="Start"
                            value={createStart}
                            onChange={(value) => {
                                const difference = createEnd.toDate().getTime() - createStart.toDate().getTime();
                                setCreateStart(value);
                                setCreateEnd(dayjs(new Date(value.toDate().getTime() + difference)));
                            }}
                        />

                        <ButtonGroup
                            sx={{
                                ml: '10px',
                            }}
                        >
                            <Button
                                onClick={() => setCreateStart((oldStart) => {
                                    const newStart = oldStart.toDate();
                                    newStart.setHours(0, 0, 0, 1);
                                    return dayjs(newStart);
                                })}
                            >
                                Beginning
                            </Button>
                            <Button
                                onClick={() => setCreateStart((oldStart) => {
                                    const newStart = oldStart.toDate();
                                    newStart.setHours(12, 0, 0, 0);
                                    return dayjs(newStart);
                                })}
                            >
                                Noon
                            </Button>
                        </ButtonGroup>
                    </Stack>

                    <Stack
                        direction="row"
                        sx={{
                            mb: '20px',
                        }}
                    >
                        <DateTimePicker
                            label="End"
                            value={createEnd}
                            onChange={(value) => setCreateEnd(value)}
                        />

                        <ButtonGroup
                            sx={{
                                ml: '10px',
                            }}
                        >
                            <Button
                                onClick={() => setCreateEnd((oldEnd) => {
                                    const newEnd = oldEnd.toDate();
                                    newEnd.setHours(23, 59, 59, 999);
                                    return dayjs(newEnd);
                                })}
                            >
                                Midnight
                            </Button>
                        </ButtonGroup>
                    </Stack>

                    {createType === 'Event' &&
                        <ButtonGroup
                            sx={{
                                display: 'block',
                                mb: '20px'
                            }}
                        >
                            <Button
                                variant="outlined"
                                sx={{
                                    display: 'inline-block',
                                    color: '#888',
                                    borderColor: '#888',
                                    cursor: 'default'
                                }}
                            >
                                Repeat...
                            </Button>
                            <Button
                                variant={createRecurringType === null ? "contained" : "outlined"}
                                sx={{
                                    display: 'inline-block',
                                }}
                                onClick={() => {
                                    setCreateRecurringType(null);
                                    setCreateRecurringParameter('');
                                }}
                            >
                                None
                            </Button>
                            <Button
                                variant={createRecurringType === 'W' ? "contained" : "outlined"}
                                sx={{
                                    display: 'inline-block',
                                }}
                                onClick={() => {
                                    setCreateRecurringType('W');
                                    setCreateRecurringParameter(`${createStart.day() + 1}`);
                                }}
                            >
                                Weekly
                            </Button>
                            <Button
                                variant={createRecurringType === 'Y' ? "contained" : "outlined"}
                                sx={{
                                    display: 'inline-block',
                                }}
                                onClick={() => {
                                    setCreateRecurringType('Y');
                                    setCreateRecurringParameter(`${createStart.month()}-${createStart.date()}`)
                                }}
                            >
                                Annually
                            </Button>
                        </ButtonGroup>
                    }

                    {createType === 'Event' && createRecurringType !== null &&
                        <>
                            {createRecurringType === 'W' ? (
                                <ButtonGroup
                                    sx={{
                                        display: 'block',
                                        mb: '20px'
                                    }}
                                >
                                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => {
                                        const dayNumber = index + 1;
                                        const included = createRecurringParameter.includes(dayNumber.toString());

                                        const click = () => {
                                            if (included) {
                                                setCreateRecurringParameter((currentParameter) => currentParameter.replaceAll(dayNumber.toString(), ''));
                                            } else {
                                                setCreateRecurringParameter((currentParameter) => currentParameter + dayNumber);
                                            }
                                        };

                                        return (
                                            <Button
                                                key={day + index}
                                                variant={included ? "contained" : "outlined"}
                                                sx={{
                                                    display: 'inline-block',
                                                }}
                                                onClick={click}
                                            >
                                                {day}
                                            </Button>
                                        );
                                    })}
                                </ButtonGroup>
                            ) : createRecurringType === 'Y' ? (
                                <DateTimePicker
                                    label="Repeat annually on"
                                    value={dayjs(new Date(new Date().getFullYear(), ...createRecurringParameter.split('-').map((value) => parseInt(value))))}
                                    onChange={(value) => setCreateRecurringParameter(`${value.month()}-${value.date()}`)}
                                    views={['day', 'month']}
                                    sx={{
                                        mb: '20px'
                                    }}
                                />
                            ) : <></>
                            }
                            <Stack
                                direction="row"
                                sx={{
                                    mb: '30px',
                                }}
                            >
                                <DateTimePicker
                                    label="Recurring End Date"
                                    value={createRecurringEnd}
                                    onChange={(value) => setCreateRecurringEnd(value)}
                                    views={['day', 'month', 'year']}
                                />

                                <ButtonGroup
                                    sx={{
                                        ml: '10px',
                                    }}
                                >
                                    <Button
                                        onClick={() => setCreateRecurringEnd(null)}
                                    >
                                        None
                                    </Button>
                                </ButtonGroup>
                            </Stack>
                        </>
                    }

                    <FormControl
                        sx={{
                            mb: '20px',
                        }}
                        fullWidth
                    >
                        <InputLabel>Labels</InputLabel>
                        <Select
                            variant="outlined"
                            input={<OutlinedInput label="Labels" />}
                            value={createLabels}
                            onChange={(e) => setCreateLabels(e.target.value)}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((labelKey) => {
                                        const label = labels.find((label) => label.key === labelKey);

                                        return label ?
                                            <Chip key={labelKey} label={label.name} sx={{ color: 'white', backgroundColor: label.color || '#454545' }} /> :
                                            undefined;
                                    })}
                                </Box>
                            )}
                            multiple
                        >
                            {labels.map((label) => (
                                <MenuItem
                                    key={label.key}
                                    value={label.key}
                                >
                                    <Circle
                                        sx={{
                                            color: label.color,
                                            mr: '10px',
                                        }}
                                    /> {label.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {createType !== 'Task' &&
                        <TextField
                            label="Location (optional)"
                            value={createLocation}
                            onChange={(e) => setCreateLocation(e.target.value)}
                            sx={{
                                mb: '20px',
                            }}
                            fullWidth
                        />
                    }

                    <TextField
                        label="Description (optional)"
                        value={createDescription}
                        onChange={(e) => setCreateDescription(e.target.value)}
                        minRows={3}
                        sx={{
                            mb: '20px',
                        }}
                        multiline
                        fullWidth
                    />

                    <Button
                        variant="contained"
                        onClick={submitCreate}
                        sx={{
                            float: 'right',
                        }}
                    >
                        Done
                    </Button>

                    {createEditKey !== null && (
                        <Button
                            variant="contained"
                            onClick={submitDelete()}
                            sx={{
                                float: 'right',
                                mr: '10px',
                            }}
                            color="error"
                        >
                            Delete
                        </Button>
                    )}

                    <Button
                        onClick={() => setCreateMenuOpen(false)}
                        sx={{
                            float: 'right',
                            mr: '10px',
                        }}
                        color="primary"
                    >
                        Cancel
                    </Button>
                </Box>
            </Modal>
        </>
    );
}